import { DocumentType } from "@api/graphql/generated/generated-types";
import { Label, Select } from "@src/common/components";
import { DOCUMENT_TYPE_LABELS } from "@src/driverDocuments/constants/documentTypeLabels";
import { QuestionMarkCircleIcon } from "@heroicons/react/24/outline";
import { Tooltip } from "flowbite-react";

const DOCUMENT_TYPE_OPTIONS = Object.entries(DOCUMENT_TYPE_LABELS).map(
  ([value, label]) => ({ id: value, label }),
);

interface Props {
  requiredDocTypes: DocumentType[];
  setRequiredDocTypes: (types: DocumentType[]) => void;
  warnedDocTypes: DocumentType[];
  setWarnedDocTypes: (types: DocumentType[]) => void;
}

export function OrderCredentialsInputs({
  requiredDocTypes,
  setRequiredDocTypes,
  warnedDocTypes,
  setWarnedDocTypes,
}: Props) {
  const toSelectValue = (types: DocumentType[]) =>
    DOCUMENT_TYPE_OPTIONS.filter((o) => types.includes(o.id as DocumentType));

  const warnedSet = new Set<string>(warnedDocTypes);
  const requiredSet = new Set<string>(requiredDocTypes);

  const requiredOptions = DOCUMENT_TYPE_OPTIONS.filter(
    (o) => !warnedSet.has(o.id),
  );
  const warnedOptions = DOCUMENT_TYPE_OPTIONS.filter(
    (o) => !requiredSet.has(o.id),
  );

  return (
    <>
      <div className="col-span-6 flex flex-col">
        <div className="flex items-center gap-1">
          <Label className="text-xs dark:text-gray-300">
            Required Certifications
          </Label>
          <Tooltip content="Drivers missing these certifications will be blocked from being assigned to this order.">
            <QuestionMarkCircleIcon className="h-3.5 w-3.5 text-gray-400 cursor-pointer" />
          </Tooltip>
        </div>
        <Select
          multi
          size="mini"
          options={requiredOptions}
          placeholder="None"
          value={toSelectValue(requiredDocTypes)}
          onChange={(params) =>
            setRequiredDocTypes(
              params.value.map((v) => v.id as DocumentType),
            )
          }
          labelKey="label"
          valueKey="id"
        />
      </div>
      <div className="col-span-6 flex flex-col">
        <div className="flex items-center gap-1">
          <Label className="text-xs dark:text-gray-300">
            Recommended Certifications
          </Label>
          <Tooltip content="Dispatchers will see a warning when assigning drivers missing these certifications, but can proceed.">
            <QuestionMarkCircleIcon className="h-3.5 w-3.5 text-gray-400 cursor-pointer" />
          </Tooltip>
        </div>
        <Select
          multi
          size="mini"
          options={warnedOptions}
          placeholder="None"
          value={toSelectValue(warnedDocTypes)}
          onChange={(params) =>
            setWarnedDocTypes(
              params.value.map((v) => v.id as DocumentType),
            )
          }
          labelKey="label"
          valueKey="id"
        />
      </div>
    </>
  );
}
